// Sektionen-Definition fuer Rail + TopBar. Single Source of Truth
// fuer "welche Top-Sektionen gibt's, wo zeigen sie hin und welche
// URL-Prefixe gehoeren dazu". Die Rail rendert die Liste als Icons,
// die TopBar nutzt sectionForPath/buildCrumbs fuer ihren Breadcrumb.

import type { IconName } from "./Icon.svelte";
import { findRoute, sections as routeSections } from "./routes";

export type SectionId =
  | "home"
  | "werkstatt"
  | "infra"
  | "sec"
  | "argus"
  | "system"
  | "daten"
  | "claude";

export interface NavSection {
  id: SectionId;
  label: string;
  // Primaer-URL: Klick auf das Section-Icon der Rail oder das Section-
  // Crumb landet hier (die Section-Hubs).
  href: string;
  // Pfad-Prefixe, die zur Section gehoeren. Mehrere wenn das URL-
  // Schema mischt (Sicherheit hat /security + /sicherheit).
  prefixes: string[];
  // Title-String der Sektion in routes.ts -- darueber findet das
  // Subpanel/Cmd-K die Sub-Pages.
  routesTitle: string;
  // Symbol der Sektion -- EINE Quelle fuer Leiste, Tabs, Baum und die
  // Telefon-Leiste. Bis 03.10.2026 standen drei Listen nebeneinander, und
  // am Schreibtisch trugen Startseite und Zuhause dasselbe Haus, System
  // und Einstellungen dasselbe Zahnrad.
  icon: IconName;
}

// Nav-Konzept 2026-07-18: fuenf Sektionen nach Objektwelt ("wo lebt
// das Ding?"): Zuhause / Infrastruktur / Sicherheit / System / Daten.
// "IoT" und "Diagnose" sind als Sektionsnamen entfallen; Aufgaben+
// Diagnose leben gemeinsam unter System. Dazu kamen Claude (16.10.2026),
// Werkstatt (18.10.2026) und Argus (28.10.2026). Die Reihenfolge hier ist die der Leiste
// und der Tabs (shell/tab-ordnung.ts, BEREICHS_FOLGE).
export const navSections: NavSection[] = [
  {
    id: "home",
    label: "Zuhause",
    href: "/zuhause",
    prefixes: ["/zuhause"],
    routesTitle: "Zuhause",
    icon: "bulb",
  },
  // Werkstatt (18.10.2026, Tim): was gebaut wird -- Konstruktionen (CAD,
  // docs/konstruktionen.md) und der 3D-Drucker (docs/drucker.md, bis dahin
  // unter Zuhause); später der Slicer. Der Würfel steht für das Teil, das
  // hier entsteht.
  {
    id: "werkstatt",
    label: "Werkstatt",
    href: "/werkstatt",
    prefixes: ["/werkstatt"],
    routesTitle: "Werkstatt",
    icon: "box",
  },
  {
    id: "infra",
    label: "Infrastruktur",
    href: "/infra",
    prefixes: ["/infra"],
    routesTitle: "Infrastruktur",
    icon: "server",
  },
  {
    id: "sec",
    label: "Sicherheit",
    href: "/sicherheit",
    prefixes: ["/security", "/sicherheit"],
    routesTitle: "Sicherheit",
    icon: "shield",
  },
  // Argus (28.10.2026, Tim): der Modellbau für sein Kamerasystem als Modul
  // (module/argus, internal/argus, docs/argus-modul.md). Das Symbol ist das
  // Argus-Auge aus dessen Favicon, nicht die Kamera -- „das könnte schnell
  // in die Irre führen".
  {
    id: "argus",
    label: "Argus",
    href: "/argus",
    prefixes: ["/argus"],
    routesTitle: "Argus",
    icon: "argus",
  },
  {
    id: "system",
    label: "System",
    href: "/system",
    prefixes: ["/system"],
    routesTitle: "System",
    icon: "wrench",
  },
  {
    id: "daten",
    label: "Daten",
    href: "/daten",
    prefixes: ["/daten"],
    routesTitle: "Daten",
    // Der Ordner: Ablage ist das Kernding der Daten. `hdd` heißt Platte und
    // Pool (Infrastruktur) -- ein Bereichssymbol steht nur für seinen
    // Bereich (docs/symbole.md, Regel 2; Tim 20.10.2026).
    icon: "folder",
  },
  // Claude im Homelab (16.10.2026, docs/claude.md): ein eigener Bereich,
  // „ein Claude Icon in der Seitenleiste und dann im Baum die Chats und den
  // Verbrauch ähnlich wie in VSCode" (Tim).
  {
    id: "claude",
    label: "Claude",
    href: "/claude",
    prefixes: ["/claude"],
    routesTitle: "Claude",
    icon: "claude",
  },
];

// sectionForPath findet die Sektion, zu der ein Pfad gehoert. Erstes
// Match per Prefix gewinnt -- die Reihenfolge in navSections ist
// daher relevant (spezifisch vor generisch, falls je noetig).
export function sectionForPath(path: string): NavSection | null {
  for (const s of navSections) {
    for (const p of s.prefixes) {
      if (path === p || path.startsWith(p + "/")) {
        return s;
      }
    }
  }
  return null;
}

// activeSectionId: bequeme ID-Variante fuer Rail's Active-State.
export function activeSectionId(path: string): SectionId | null {
  return sectionForPath(path)?.id ?? null;
}

// Crumb fuer den TopBar-Breadcrumb. Klickbarkeit per href; leaf=true
// markiert das letzte Element (aktuelle Page, kein Link).
export interface Crumb {
  label: string;
  href?: string;
  leaf?: boolean;
}

// buildCrumbs leitet aus dem aktuellen Pfad eine flache Liste an
// Breadcrumb-Eintraegen ab. Konvention:
//   - Auf "/" leer (TopBar zeigt nur das Brand)
//   - Sonst: Section (klickbar zum Sektions-Primaer-URL) + aktuelle
//     Page als Leaf. Wenn Section-Primaer == aktuelle Page, dann
//     reicht das Section-Label als Leaf -- kein Doppel-Eintrag.
//   - Fuer tiefe Pfade wie /zuhause/wetter/blitze fuegen wir die
//     Zwischenebenen ein, sofern sie als nicht-versteckte Routen
//     existieren. So sieht Tim "IoT > Wetter > Blitze".
export function buildCrumbs(path: string): Crumb[] {
  if (!path || path === "/") return [];

  const section = sectionForPath(path);
  const out: Crumb[] = [];

  if (section) {
    out.push({ label: section.label, href: section.href });
  }

  // Zwischenebenen sammeln: walk durch die Pfad-Segmente und schaue,
  // ob ein Prefix als Route existiert. Wir ueberspringen die Section-
  // Primary (schon drin) und die aktuelle Page (kommt als Leaf).
  const segments = path.split("/").filter(Boolean);
  let acc = "";
  for (let i = 0; i < segments.length - 1; i++) {
    acc += "/" + segments[i];
    if (acc === section?.href) continue;
    const m = findRoute(acc);
    if (m && m.route.path === acc) {
      out.push({ label: m.route.label, href: acc });
    }
  }

  // Aktuelle Page als Leaf (nur wenn nicht == Section-Primary).
  const cur = findRoute(path);
  if (cur && cur.route.path !== section?.href) {
    out.push({ label: cur.route.label, leaf: true });
  } else if (!cur && segments.length > 0) {
    // Unbekannte Sub-Page (z.B. /zuhause/aktoren/foo mit Param-Match):
    // letztes Segment als Roh-Leaf zeigen, damit der User den Pfad
    // nicht "abrupt enden" sieht.
    out.push({ label: decodeURIComponent(segments[segments.length - 1]), leaf: true });
  } else if (out.length === 1 && cur && cur.route.path === section?.href) {
    // Section-Primary == current: kein Doppel-Crumb, das Section-Label
    // wird selbst zum Leaf.
    out[0] = { label: section!.label, leaf: true };
  }

  return out;
}

// subPagesFor liefert die Sub-Pages einer Sektion fuer Cmd-K /
// QuickSwitcher / Overview-Pages. Filter wie im QuickSwitcher: keine
// Param-Routen, keine inMenu=false-Aliase.
export interface SubPage {
  label: string;
  href: string;
  hidden: boolean;
  /** Nur bei Registern: die Seite, zu der sie gehören. */
  tabWurzel?: string;
}
/** registerFor: die Register einer Sektion (Routen mit tabWurzel, z.B.
 *  Netzwerk → Verlauf, Cluster → Inventar). Der Explorer-Baum hängt sie
 *  unter ihre Seite -- seit 02.10.2026, als die Übersichtsseiten am
 *  Schreibtisch wegfielen und Link-Health nur noch über die Suche zu
 *  finden war (Tim: „alles links im Baum"). */
export function registerFor(s: NavSection): SubPage[] {
  const sec = routeSections.find((r) => r.title === s.routesTitle);
  if (!sec) return [];
  return sec.items
    .filter((r) => !r.path.includes(":") && r.tabWurzel)
    .map((r) => ({ label: r.label, href: r.path, hidden: !!r.hidden, tabWurzel: r.tabWurzel }));
}

export function subPagesFor(s: NavSection): SubPage[] {
  const sec = routeSections.find((r) => r.title === s.routesTitle);
  if (!sec) return [];
  return sec.items
    .filter((r) => !r.path.includes(":") && r.inMenu !== false)
    .map((r) => ({
      label: r.label,
      href: r.path,
      hidden: !!r.hidden,
    }));
}
